/* ============================================
   PostCard — Compact post card for calendar grid
   Shows channel icons, post type, caption, status.
   ============================================ */
import type { SocialPost, SocialChannel, SocialPostStatus } from '@/lib/types'

interface PostCardProps {
  post: SocialPost
  compact?: boolean
  onClick?: () => void
}

const CHANNEL_COLORS: Record<SocialChannel, string> = {
  instagram: '#E4405F',
  facebook: '#1877F2',
  twitter: '#000000',
  pinterest: '#E60023',
}

// Extended for new platforms
const CHANNEL_COLORS_EXT: Record<string, string> = {
  ...CHANNEL_COLORS,
  tiktok: '#000000',
  linkedin: '#0A66C2',
}

const POST_TYPE_SHORT: Record<string, string> = {
  image: 'IMG',
  carousel: 'CARU',
  reel: 'REEL',
  story: 'STORY',
  video: 'VIDEO',
  text: 'TXT',
}

const STATUS_CONFIG: Record<SocialPostStatus, { label: string; className: string }> = {
  proposal: { label: 'Propuesta IA', className: 'sm-badge--proposal' },
  draft: { label: 'Borrador', className: 'sm-badge--draft' },
  pending_review: { label: 'Revisión', className: 'sm-badge--review' },
  approved: { label: 'Aprobado', className: 'sm-badge--approved' },
  scheduled: { label: 'Programado', className: 'sm-badge--scheduled' },
  published: { label: 'Publicado', className: 'sm-badge--published' },
  rejected: { label: 'Rechazado', className: 'sm-badge--rejected' },
  error: { label: 'Error', className: 'sm-badge--error' },
}

function formatTime(dateStr: string | null): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
}

function ChannelDot({ channel }: { channel: string }) {
  const color = CHANNEL_COLORS_EXT[channel] || '#888'
  const initial = channel === 'instagram' ? 'IG'
    : channel === 'facebook' ? 'FB'
    : channel === 'twitter' ? 'X'
    : channel === 'tiktok' ? 'TK'
    : channel === 'linkedin' ? 'LI'
    : channel === 'pinterest' ? 'PI'
    : channel.slice(0, 2).toUpperCase()

  return (
    <span
      className="sm-post-card__channel-tag"
      style={{ background: color }}
      title={channel}
    >
      {initial}
    </span>
  )
}

export default function PostCard({ post, compact = false, onClick }: PostCardProps) {
  const statusCfg = STATUS_CONFIG[post.status] ?? STATUS_CONFIG.draft
  const caption = post.caption || '(Sin texto)'
  const maxLen = compact ? 30 : 40
  const truncated = caption.length > maxLen ? caption.slice(0, maxLen) + '…' : caption
  const typeLabel = POST_TYPE_SHORT[post.post_type] || post.post_type?.toUpperCase() || 'POST'
  const hasImage = post.media_urls && post.media_urls.length > 0
  const hasImagePrompt = !!post.image_prompt
  const needsImage = hasImagePrompt && !hasImage && post.status === 'draft'

  return (
    <div
      className={`sm-post-card ${compact ? 'sm-post-card--compact' : ''} sm-post-card--${post.status}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter') onClick() } : undefined}
    >
      {/* Image thumbnail */}
      {hasImage && !compact && (
        <div className="sm-post-card__thumb">
          <img src={post.media_urls[0]} alt="" className="sm-post-card__thumb-img" />
        </div>
      )}

      {/* Needs image indicator */}
      {needsImage && !compact && (
        <div className="sm-post-card__needs-image" title="Imagen pendiente de generar">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
          </svg>
        </div>
      )}

      {/* Channel tags + post type */}
      <div className="sm-post-card__top-row">
        <div className="sm-post-card__channels">
          {(post.channels ?? []).map(ch => (
            <ChannelDot key={ch} channel={ch} />
          ))}
        </div>
        <span className="sm-post-card__type-tag">{typeLabel}</span>
      </div>

      {/* Caption */}
      <div className="sm-post-card__caption" title={post.caption}>
        {truncated}
      </div>

      {/* Footer: time + status */}
      <div className="sm-post-card__footer">
        {post.publication_date && (
          <span className="sm-post-card__time">{formatTime(post.publication_date)}</span>
        )}
        <span className={`sm-badge ${statusCfg.className}`}>
          {statusCfg.label}
        </span>
      </div>
    </div>
  )
}

export { CHANNEL_COLORS, CHANNEL_COLORS_EXT, STATUS_CONFIG }
